import { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { MessageTypes } from '../../../interfaces/taraInterfaces';
import TaraBarChart from '../../../lib/constants/taraDummyData/TaraBarChart';
import TaraLineChart from '../../../lib/constants/taraDummyData/TaraLineChart';
import TaraPieChart from '../../../lib/constants/taraDummyData/TaraPieChart';
import MarkdownMessage from './MarkdownMessage';
import '../../../styles/pages/tara/chat/taraChatItemAI.scss';
import { APP_ENV } from '../../../lib/constants/central-api';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import DownArrow from '../../../assets/svg/DownArrow';
import { t } from 'i18next';
import {
  copySVG,
  filledCheckboxSVG,
  thumbUpSVG,
  thumbDownSVG,
  sendSVG,
  saveSVG,
  warningSVG,
  downloadSVG,
} from '../../../lib/allCustomSVGs';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import tara_chat_feedback_api_call from '../../../lib/api/tara/tara-chat-feedback-api-call';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../store/reducers/appSlice';
import save_graph_prompt_from_chat_api_call from '../../../lib/api/custom-prompts/save-graph-prompt-from-chat-api-call';
import CircularProgress from '@mui/material/CircularProgress';

type Props = {
  message: any;
  messageType: MessageTypes;
  isTyping?: boolean;
  debugInfo?: string | null;
  chatIndex?: number;
  chatMessages?: any;
  responseId?: string | null;
  feedbackReaction?: boolean | null;
  conversationId?: string | null;
};

const TaraChatItemAI = ({
  message,
  messageType,
  isTyping = false,
  debugInfo,
  chatIndex,
  chatMessages,
  responseId,
  feedbackReaction,
  conversationId,
}: Props) => {
  const dispatch = useDispatch();
  const [isDebugInfoVisible, setIsDebugInfoVisible] = useState(false);
  const [isOlderMessagesExpanded, setIsOlderMessagesExpanded] = useState(false);
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const [isFeedbackBoxVisible, setIsFeedbackBoxVisible] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [selectedReaction, setSelectedReaction] = useState<
    'up' | 'down' | null
  >(
    feedbackReaction === true
      ? 'up'
      : feedbackReaction === false
      ? 'down'
      : null
  );
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isSavingGraph, setIsSavingGraph] = useState(false);
  const [isDownloadingChart, setIsDownloadingChart] = useState(false);
  const [isGraphSaved, setIsGraphSaved] = useState(false);
  const [hasGraphError, setHasGraphError] = useState(false);
  const [isGraphHovered, setIsGraphHovered] = useState(false);
  const [isDownloadHovered, setIsDownloadHovered] = useState(false);
  const [graphTooltipPosition, setGraphTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const [downloadTooltipPosition, setDownloadTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const debugInfoRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const feedbackBoxRef = useRef<HTMLDivElement | null>(null);
  const graphButtonRef = useRef<HTMLButtonElement | null>(null);
  const downloadButtonRef = useRef<HTMLButtonElement | null>(null);
  const graphTooltipRef = useRef<HTMLSpanElement | null>(null);
  const downloadTooltipRef = useRef<HTMLSpanElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);

  const streamingStatusMessages = useSelector(
    (state: RootState) => state.chat.streamingStatusMessages
  );

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [streamingStatusMessages]);

  // Handle click outside to hide debug info
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        debugInfoRef.current &&
        !debugInfoRef.current.contains(event.target as Node)
      ) {
        setIsDebugInfoVisible(false);
      }
    };

    if (isDebugInfoVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDebugInfoVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        feedbackBoxRef.current &&
        !feedbackBoxRef.current.contains(event.target as Node)
      ) {
        setIsFeedbackBoxVisible(false);
        setFeedbackComment('');
      }
    };

    if (isFeedbackBoxVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFeedbackBoxVisible]);

  useEffect(() => {
    if (feedbackReaction === true) {
      setSelectedReaction('up');
    } else if (feedbackReaction === false) {
      setSelectedReaction('down');
    } else {
      setSelectedReaction(null);
    }
  }, [feedbackReaction]);

  const handleCopyToClipboard = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopyClicked(true);

      setTimeout(() => {
        setIsCopyClicked(false);
      }, 800);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getCopyableText = () => {
    if (typeof message === 'string') {
      return message;
    }
    if (message && typeof message === 'object') {
      return JSON.stringify(message, null, 2);
    }
    return '';
  };

  const getCopyableTableText = (table: any) => {
    let tableObj = table;

    if (typeof table === 'string') {
      try {
        tableObj = JSON.parse(table);
      } catch (error) {
        return '';
      }
    }

    if (
      !tableObj ||
      !Array.isArray(tableObj.columns) ||
      !Array.isArray(tableObj.rows)
    ) {
      return '';
    }

    const { title, columns, rows } = tableObj;

    // Format as CSV for easy copying
    let csvText = '';

    if (title) {
      csvText += `${title}\n\n`;
    }

    // Add column headers
    csvText += columns.join(',') + '\n';

    // Add rows
    rows.forEach((row: any[]) => {
      // Escape commas and quotes in cell values
      const escapedRow = row.map((cell: any) => {
        const cellStr = String(cell || '');
        if (
          cellStr.includes(',') ||
          cellStr.includes('"') ||
          cellStr.includes('\n')
        ) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      });
      csvText += escapedRow.join(',') + '\n';
    });

    return csvText.trim();
  };

  /* const renderChart = (chart: any) => {
    if (chart?.type === 'line') {
      return <TaraLineChart title={chart?.title} chartData={chart} />;
    } else if (chart?.type === 'bar' || chart?.type === 'stacked-bar') {
      return <TaraBarChart title={chart?.title} chartData={chart} />;
    } else if (chart?.type === 'pie') {
      return <TaraPieChart title={chart?.title} chartData={chart} />;
    }
    return null;
  }; */

  const renderChart = (chart: any) => {
    let chartObj = chart;

    // Attempt to parse if chart is a string
    if (typeof chart === 'string') {
      try {
        chartObj = JSON.parse(chart);
      } catch (error) {
        console.error('Invalid JSON string passed to renderChart:', error);
        return null; // Prevent app from breaking
      }
    }

    if (chartObj?.type === 'line') {
      return <TaraLineChart title={chartObj?.title} chartData={chartObj} />;
    } else if (chartObj?.type === 'bar' || chartObj?.type === 'stacked-bar') {
      return <TaraBarChart title={chartObj?.title} chartData={chartObj} />;
    } else if (chartObj?.type === 'pie') {
      return <TaraPieChart title={chartObj?.title} chartData={chartObj} />;
    }

    return null;
  };

  const renderTable = (table: any) => {
    // Handle both object and string formats
    let tableObj = table;

    if (typeof table === 'string') {
      try {
        tableObj = JSON.parse(table);
      } catch (error) {
        console.error('Invalid JSON string passed to renderTable:', error);
        return null;
      }
    }

    if (
      !tableObj ||
      !Array.isArray(tableObj.columns) ||
      !Array.isArray(tableObj.rows)
    ) {
      console.error('Invalid table structure:', tableObj);
      return null;
    }

    const { title, columns, rows } = tableObj;

    return (
      <div className='taraChatItemAI__table-container'>
        {title && <h3 className='taraChatItemAI__table-title'>{title}</h3>}
        <div className='taraChatItemAI__table-wrapper'>
          <table>
            <thead>
              <tr>
                {columns.map((column: string, index: number) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any[], rowIndex: number) => (
                <tr key={rowIndex}>
                  {row.map((cell: any, cellIndex: number) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='taraChatItemAI__copy-container'>
          <button
            className='taraChatItemAI__copy-button'
            data-copied={isCopyClicked.toString()}
            onClick={() =>
              handleCopyToClipboard(getCopyableTableText(tableObj))
            }
            title={t('CopyToClipboard')}
          >
            {!isCopyClicked ? (
              <CustomSVGs svg={copySVG} />
            ) : (
              <CustomSVGs svg={filledCheckboxSVG} />
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderTextMessageContent = () => {
    if (message && typeof message === 'string') {
      return (
        <div className='taraChatItemAI__message-content'>
          <MarkdownMessage content={message} />
          <div className='taraChatItemAI__copy-container'>
            <button
              className='taraChatItemAI__copy-button'
              data-copied={isCopyClicked.toString()}
              onClick={() => handleCopyToClipboard(message)}
              title={t('CopyToClipboard')}
            >
              {!isCopyClicked ? (
                <CustomSVGs svg={copySVG} />
              ) : (
                <CustomSVGs svg={filledCheckboxSVG} />
              )}
            </button>
          </div>
        </div>
      );
    }

    if (message && typeof message === 'object') {
      const stringifiedMessage = JSON.stringify(message, null, 2);
      return (
        <div className='taraChatItemAI__message-content'>
          <MarkdownMessage content={stringifiedMessage} />
          <div className='taraChatItemAI__copy-container'>
            <button
              className='taraChatItemAI__copy-button'
              data-copied={isCopyClicked.toString()}
              onClick={() => handleCopyToClipboard(stringifiedMessage)}
              title={t('CopyToClipboard')}
            >
              {!isCopyClicked ? (
                <CustomSVGs svg={copySVG} />
              ) : (
                <CustomSVGs svg={filledCheckboxSVG} />
              )}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderChat = () => {
    if (messageType === 'chart' && message) {
      const showSaveButton = !!(conversationId && responseId && !isTyping);
      const showDownloadButton = !isTyping;
      return (
        <div
          className='taraChatItemAI__chart'
          data-is-typing={isTyping ? 'true' : 'false'}
        >
          {(showSaveButton || showDownloadButton) && (
            <div className='taraChatItemAI__chart-save-container'>
              {showDownloadButton && (
                <div
                  className='taraChatItemAI__chart-save-button-wrapper'
                  onMouseEnter={() => {
                    setIsDownloadHovered(true);
                  }}
                  onMouseLeave={() => setIsDownloadHovered(false)}
                >
                  {isDownloadHovered && (
                    <span
                      ref={downloadTooltipRef}
                      className='taraChatItemAI__chart-save-button-tooltip'
                      style={{
                        top: `${downloadTooltipPosition.top}px`,
                        left: `${downloadTooltipPosition.left}px`,
                      }}
                    >
                      {isDownloadingChart
                        ? t('DownloadingGraph')
                        : t('DownloadAsImage')}
                    </span>
                  )}
                  <button
                    ref={downloadButtonRef}
                    className='taraChatItemAI__chart-save-button'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadChart();
                    }}
                    disabled={isDownloadingChart}
                  >
                    {isDownloadingChart ? (
                      <CircularProgress
                        size={18}
                        style={{ color: 'var(--main-text-color)' }}
                      />
                    ) : (
                      <CustomSVGs svg={downloadSVG} />
                    )}
                  </button>
                </div>
              )}
              {showSaveButton && (
                <div
                  className='taraChatItemAI__chart-save-button-wrapper'
                  onMouseEnter={() => {
                    setIsGraphHovered(true);
                  }}
                  onMouseLeave={() => setIsGraphHovered(false)}
                >
                  {isGraphHovered && (
                    <span
                      ref={graphTooltipRef}
                      className='taraChatItemAI__chart-save-button-tooltip'
                      style={{
                        top: `${graphTooltipPosition.top}px`,
                        left: `${graphTooltipPosition.left}px`,
                      }}
                    >
                      {isGraphSaved
                        ? t('CustomGraphPromptSaved')
                        : isSavingGraph
                        ? t('SavingCustomGraphPrompt')
                        : hasGraphError
                        ? t('ErrorSavingCustomGraphPrompt')
                        : t('SaveAsCustomGraphPrompt')}
                    </span>
                  )}
                  <button
                    ref={graphButtonRef}
                    className='taraChatItemAI__chart-save-button'
                    data-saved={isGraphSaved.toString()}
                    data-error={hasGraphError.toString()}
                    data-saving={isSavingGraph.toString()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveGraph();
                    }}
                    disabled={isSavingGraph || isGraphSaved}
                  >
                    {isGraphSaved ? (
                      <CustomSVGs svg={filledCheckboxSVG} />
                    ) : isSavingGraph ? (
                      <CircularProgress
                        size={18}
                        style={{ color: 'var(--main-text-color)' }}
                      />
                    ) : hasGraphError ? (
                      <CustomSVGs svg={warningSVG} />
                    ) : (
                      <CustomSVGs svg={saveSVG} />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            ref={chartContainerRef}
            className='taraChatItemAI__chart-content'
          >
            {renderChart(message)}
          </div>
        </div>
      );
    } else if (messageType === 'table' && message) {
      return (
        <div
          className='taraChatItemAI__table'
          data-is-typing={isTyping ? 'true' : 'false'}
        >
          {renderTable(message)}
        </div>
      );
    } else if (messageType === 'text' && typeof message === 'string') {
      return (
        <div
          className='taraChatItemAI__message'
          data-is-typing={isTyping ? 'true' : 'false'}
        >
          {renderTextMessageContent()}
        </div>
      );
    }
    return null;
  };

  const renderStatusMessages = (messages: string[]) => {
    if (!Array.isArray(messages) || messages.length === 0) return null;

    const recentMessages = isTyping ? messages.slice(-5) : [];
    const olderMessages = isTyping ? messages.slice(0, -5) : messages;

    const renderMessageItem = (
      message: string,
      index: number,
      isLast: boolean,
      isOlder: boolean = false
    ) => (
      <div
        key={index}
        className={`taraChatItemAI__message--loading-text${
          !isLast || (isOlder && recentMessages.length > 0)
            ? ' with-connector'
            : ''
        }`}
      >
        {!isLast || !isTyping || isOlder ? (
          <span className='taraChatItemAI__tick'>&#10003;</span>
        ) : (
          <div className='taraChatItemAI__loading-circle'></div>
        )}
        <span className='taraChatItemAI__message--loading-text-content'>
          {message}
        </span>
      </div>
    );

    return (
      <>
        {(olderMessages.length > 0 || !isTyping) && (
          <div
            className={`taraChatItemAI__older-messages ${
              isOlderMessagesExpanded ? 'expanded' : 'collapsed'
            }`}
          >
            <button
              className='taraChatItemAI__expand-button'
              onClick={() =>
                setIsOlderMessagesExpanded(!isOlderMessagesExpanded)
              }
            >
              {isOlderMessagesExpanded ? (
                <>
                  {t('CollapseSteps')}
                  <DownArrow
                    className='taraChatItemAI__expand-arrow'
                    style={{ transform: 'rotate(180deg)' }}
                  />
                </>
              ) : (
                <>
                  {t('View')} {olderMessages.length}{' '}
                  {isTyping ? t('Previous') + ' ' : ''}
                  {t('Steps')}
                  <DownArrow className='taraChatItemAI__expand-arrow' />
                </>
              )}
            </button>
            <div className='taraChatItemAI__older-messages-content'>
              {olderMessages.map((msg, idx) =>
                renderMessageItem(
                  msg,
                  idx,
                  idx === olderMessages.length - 1,
                  true
                )
              )}
            </div>
          </div>
        )}
        {recentMessages.map((msg, idx) =>
          renderMessageItem(
            msg,
            idx + olderMessages.length,
            idx === recentMessages.length - 1
          )
        )}
      </>
    );
  };

  const handleThumbUp = async () => {
    if (!responseId || isTyping || selectedReaction !== null) return;

    setSelectedReaction('up');
    setIsFeedbackBoxVisible(false);
    setFeedbackComment('');

    try {
      setIsSubmittingFeedback(true);
      await tara_chat_feedback_api_call(responseId, true, null);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSelectedReaction(null);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleThumbDown = async () => {
    if (!responseId || isTyping || selectedReaction !== null) return;

    setSelectedReaction('down');
    setIsFeedbackBoxVisible(true);

    try {
      setIsSubmittingFeedback(true);
      await tara_chat_feedback_api_call(responseId, false, null);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSelectedReaction(null);
      setIsFeedbackBoxVisible(false);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleSendFeedback = async () => {
    if (!responseId || isTyping || selectedReaction !== 'down') return;

    try {
      setIsSubmittingFeedback(true);
      await tara_chat_feedback_api_call(
        responseId,
        false,
        feedbackComment.trim() || null
      );
      setIsFeedbackBoxVisible(false);
      setFeedbackComment('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const updateGraphTooltipPosition = () => {
    if (graphButtonRef.current && graphTooltipRef.current) {
      const buttonRect = graphButtonRef.current.getBoundingClientRect();
      const tooltipRect = graphTooltipRef.current.getBoundingClientRect();
      const left = buttonRect.left + buttonRect.width / 2;
      const top = buttonRect.top - tooltipRect.height - 0.5;
      setGraphTooltipPosition({ top, left });
    }
  };

  const updateDownloadTooltipPosition = () => {
    if (downloadButtonRef.current && downloadTooltipRef.current) {
      const buttonRect = downloadButtonRef.current.getBoundingClientRect();
      const tooltipRect = downloadTooltipRef.current.getBoundingClientRect();
      const left = buttonRect.left + buttonRect.width / 2;
      const top = buttonRect.top - tooltipRect.height - 0.5;
      setDownloadTooltipPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isGraphHovered) {
      requestAnimationFrame(() => {
        updateGraphTooltipPosition();
      });
      const handleScroll = () => updateGraphTooltipPosition();
      const handleResize = () => updateGraphTooltipPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isGraphHovered]);

  useEffect(() => {
    if (isDownloadHovered) {
      requestAnimationFrame(() => {
        updateDownloadTooltipPosition();
      });
      const handleScroll = () => updateDownloadTooltipPosition();
      const handleResize = () => updateDownloadTooltipPosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isDownloadHovered]);

  const handleDownloadChart = async () => {
    if (!chartContainerRef.current || isDownloadingChart) return;

    setIsDownloadHovered(false);
    setIsDownloadingChart(true);

    setTimeout(async () => {
      try {
        const canvas = await html2canvas(chartContainerRef.current!, {
          useCORS: true,
          backgroundColor: '#fff',
          scale: 2,
        });

        const dataUrl = canvas.toDataURL('image/png');

        const link = document.createElement('a');
        link.download = `tara-graph-${new Date().getTime()}.png`;
        link.href = dataUrl;
        link.click();

        dispatch(
          setMessage({
            message: t('GraphDownloadedSuccessfully'),
            messageType: 'success',
          })
        );
      } catch (error) {
        console.error('Error downloading chart:', error);
        dispatch(
          setMessage({
            message: t('ErrorDownloadingGraph'),
            messageType: 'error',
          })
        );
      } finally {
        setIsDownloadingChart(false);
      }
    }, 100);
  };

  const handleSaveGraph = async () => {
    if (!conversationId || !responseId || isSavingGraph || isGraphSaved) return;

    setIsGraphHovered(false);
    setIsSavingGraph(true);
    setHasGraphError(false);
    try {
      const result = await save_graph_prompt_from_chat_api_call(
        conversationId,
        responseId
      );
      if (result?.success) {
        dispatch(
          setMessage({
            message: t('CustomGraphPromptSavedSuccessfully'),
            messageType: 'success',
          })
        );
        setIsGraphSaved(true);
        setTimeout(() => {
          setIsGraphSaved(false);
        }, 2000);
      } else {
        const errorMessage = result?.message || 'Failed to save graph prompt';
        dispatch(
          setMessage({
            message: errorMessage,
            messageType: 'warning',
          })
        );
        setHasGraphError(true);
        setTimeout(() => {
          setHasGraphError(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving graph:', error);
      dispatch(
        setMessage({
          message: 'An error occurred while saving the graph prompt',
          messageType: 'error',
        })
      );
      setHasGraphError(true);
      setTimeout(() => {
        setHasGraphError(false);
      }, 3000);
    } finally {
      setIsSavingGraph(false);
    }
  };

  return (
    <div
      style={{
        marginBottom:
          messageType === 'chart' || messageType === 'table' ? '4rem' : '1rem',
      }}
      className='taraChatItemAI'
      ref={chatContainerRef}
    >
      {(isTyping ||
        (streamingStatusMessages &&
          Array.isArray(streamingStatusMessages) &&
          streamingStatusMessages.length > 0 &&
          chatIndex &&
          chatMessages &&
          chatIndex ===
            chatMessages?.findLastIndex((item: any) => item.role === 'user') +
              1)) && (
        <div
          className='taraChatItemAI__message'
          data-is-typing={isTyping ? 'true' : 'false'}
        >
          <div className='taraChatItemAI__message--loading-container'>
            {renderStatusMessages(streamingStatusMessages)}
          </div>
        </div>
      )}
      {renderChat()}
      {!isTyping && responseId && messageType !== 'chart' && (
        <div className='taraChatItemAI__feedback'>
          {(!selectedReaction || selectedReaction === 'up') && (
            <button
              className={`taraChatItemAI__feedback--thumb ${
                selectedReaction === 'up' ? 'selected' : ''
              }`}
              onClick={handleThumbUp}
              disabled={isSubmittingFeedback || selectedReaction !== null}
              title={'Good response'}
              data-selected={selectedReaction === 'up'}
            >
              <CustomSVGs svg={thumbUpSVG} />
            </button>
          )}
          {(!selectedReaction || selectedReaction === 'down') && (
            <button
              className={`taraChatItemAI__feedback--thumb ${
                selectedReaction === 'down' ? 'selected' : ''
              }`}
              onClick={handleThumbDown}
              disabled={isSubmittingFeedback || selectedReaction !== null}
              title={'Bad response'}
              data-selected={selectedReaction === 'down'}
            >
              <CustomSVGs svg={thumbDownSVG} />
            </button>
          )}
          {isFeedbackBoxVisible && selectedReaction === 'down' && (
            <div ref={feedbackBoxRef} className='taraChatItemAI__feedback--box'>
              <div className='taraChatItemAI__feedback--input-wrapper'>
                <textarea
                  className='taraChatItemAI__feedback--input'
                  placeholder={t('FeedbackPlaceholder')}
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (feedbackComment.trim() && !isSubmittingFeedback) {
                        handleSendFeedback();
                      }
                    }
                  }}
                  rows={2}
                  disabled={isSubmittingFeedback}
                />
                <button
                  className='taraChatItemAI__feedback--send'
                  onClick={handleSendFeedback}
                  disabled={isSubmittingFeedback || !feedbackComment.trim()}
                  title={t('Send')}
                >
                  <CustomSVGs svg={sendSVG} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {debugInfo && APP_ENV === 'DEV' && (
        <div ref={debugInfoRef} className='taraChatItemAI__debugInfo'>
          <span
            data-is-debug-info-visible={isDebugInfoVisible ? 'true' : 'false'}
            onClick={() => {
              setIsDebugInfoVisible(!isDebugInfoVisible);
            }}
          >
            {isDebugInfoVisible ? 'Hide Debug Info' : 'Show Debug Info'}
          </span>
          {isDebugInfoVisible ? (
            <div className='taraChatItemAI__debugInfo--content'>
              <pre>{debugInfo}</pre>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default TaraChatItemAI;
